import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, XCircle, CalendarDays, List, LayoutGrid, Plus, QrCode, Mail, Image, CalendarClock } from "lucide-react";
import { NewBookingModal } from "./NewBookingModal";
import { AdminStats, WeekView, mondayOf, toISO } from "./AdminWidgets";
import { GalleryManager } from "./GalleryManager";
import { LoyaltyManager } from "./LoyaltyManager";
import { CustomerEmails } from "./CustomerEmails";
import { euro } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bany_admin_token";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` } });

const detailText = (d) => (Array.isArray(d) ? d.map((e) => e.msg).join(" ") : d);

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      onLogin(r.data);
    } catch (err) {
      setError(detailText(err.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#0B1F3A] text-[#F7F2EC] noise-overlay relative">
      <form onSubmit={submit} data-testid="admin-login-form" className="relative z-10 w-full max-w-sm">
        <p className="font-display font-black text-6xl leading-none">BANY</p>
        <p className="font-mono-alt text-[11px] tracking-[0.4em] uppercase opacity-70 mb-10">Area titolare</p>
        <input
          data-testid="admin-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="w-full rounded-xl bg-[#F7F2EC]/10 border border-[#F7F2EC]/30 px-5 py-4 text-sm outline-none focus:border-[#F7F2EC] placeholder:text-[#F7F2EC]/50 mb-3"
        />
        <input
          data-testid="admin-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full rounded-xl bg-[#F7F2EC]/10 border border-[#F7F2EC]/30 px-5 py-4 text-sm outline-none focus:border-[#F7F2EC] placeholder:text-[#F7F2EC]/50 mb-4"
        />
        {error && <p data-testid="admin-login-error" className="text-sm bg-[#F7F2EC] text-[#0B1F3A] rounded-lg px-4 py-2 mb-4">{error}</p>}
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={busy}
          className="w-full rounded-full bg-[#F7F2EC] text-[#0B1F3A] py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Entra
        </button>
      </form>
    </div>
  );
};

const TABS = [
  { id: "agenda", label: "Agenda", icon: CalendarClock },
  { id: "tessere", label: "Tessere", icon: QrCode },
  { id: "email", label: "Email clienti", icon: Mail },
  { id: "galleria", label: "Galleria", icon: Image },
];

const Tabs = ({ tab, setTab }) => (
  <nav data-testid="admin-tabs" className="flex gap-2 overflow-x-auto mb-8 -mx-1 px-1">
    {TABS.map(({ id, label, icon: Icon }) => (
      <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
        className={`shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${tab === id ? "bg-[#0B1F3A] text-white" : "border border-[rgba(11,31,58,0.2)] text-[#0B1F3A] hover:border-[#0B1F3A]"}`}>
        <Icon className="w-4 h-4" /> <span className="tracking-[0.08em]">{label}</span>
      </button>
    ))}
  </nav>
);

const groupByDate = (list) =>
  list.reduce((acc, b) => {
    (acc[b.date] = acc[b.date] || []).push(b);
    return acc;
  }, {});

const Dashboard = ({ user, onLogout }) => {
  const [bookings, setBookings] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [week, setWeek] = useState([]);
  const [view, setView] = useState("list");
  const [monday, setMonday] = useState(() => mondayOf(new Date()));
  const [date, setDate] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState("agenda");

  const refreshAll = () => {
    fetchBookings(date ? { date } : {}).then((d) => d && setBookings(d));
    fetchBookings({}).then((d) => d && setUpcoming(d));
    loadWeek(monday);
  };

  const fetchBookings = async (params) => {
    try {
      const r = await axios.get(`${API}/admin/bookings`, { ...auth(), params });
      return r.data;
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Impossibile caricare le prenotazioni");
      return null;
    }
  };

  const loadWeek = async (m) => {
    const end = new Date(m);
    end.setDate(end.getDate() + 5);
    const data = await fetchBookings({ start: toISO(m), end: toISO(end) });
    if (data) setWeek(data);
  };

  useEffect(() => { fetchBookings(date ? { date } : {}).then((d) => d && setBookings(d)); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchBookings({}).then((d) => d && setUpcoming(d)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadWeek(monday); }, [monday]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async (b) => {
    if (!window.confirm(`Annullare la prenotazione di ${b.name} del ${b.date} alle ${b.time}?`)) return;
    setBusyId(b.id);
    try {
      await axios.post(`${API}/admin/bookings/${b.id}/cancel`, {}, auth());
      toast.success("Prenotazione annullata");
      const mark = (list) => (list || []).map((x) => (x.id === b.id ? { ...x, status: "cancelled" } : x));
      setBookings(mark);
      setUpcoming(mark);
      setWeek(mark);
    } catch (err) {
      toast.error(detailText(err.response?.data?.detail) || "Errore");
    } finally {
      setBusyId(null);
    }
  };

  const groups = groupByDate(bookings || []);
  const dates = Object.keys(groups).sort();

  return (
    <div className="min-h-screen px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-10">
      <header className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <p className="font-display font-black text-4xl leading-none text-[#0B1F3A]">BANY</p>
          <p className="font-mono-alt text-[11px] tracking-[0.4em] uppercase text-[#6F6466]">Agenda · {user.email}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="admin-new-booking-button" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#071528] transition-colors">
            <Plus className="w-4 h-4" /> Nuovo appuntamento
          </button>
          {tab === "agenda" && <div className="flex rounded-full border border-[rgba(11,31,58,0.25)] bg-white p-1 text-xs uppercase tracking-wider">
            <button data-testid="admin-view-list" onClick={() => setView("list")} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors ${view === "list" ? "bg-[#0B1F3A] text-white" : "text-[#6F6466]"}`}><List className="w-3.5 h-3.5" /> Lista</button>
            <button data-testid="admin-view-week" onClick={() => setView("week")} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors ${view === "week" ? "bg-[#0B1F3A] text-white" : "text-[#6F6466]"}`}><LayoutGrid className="w-3.5 h-3.5" /> Settimana</button>
          </div>}
          {tab === "agenda" && view === "list" && <label className="flex items-center gap-2 rounded-full border border-[rgba(11,31,58,0.25)] bg-white px-4 py-2 text-sm">
            <CalendarDays className="w-4 h-4 text-[#0B1F3A]" />
            <input data-testid="admin-date-filter" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-transparent outline-none text-sm" />
            {date && <button data-testid="admin-date-clear" onClick={() => setDate("")} className="text-xs text-[#0B1F3A] uppercase">Tutte</button>}
          </label>}
          <button data-testid="admin-logout-button" onClick={onLogout} className="inline-flex items-center gap-2 rounded-full border border-[rgba(26,20,22,0.15)] px-4 py-2 text-sm hover:border-[#0B1F3A] hover:text-[#0B1F3A] transition-colors">
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </header>

      {adding && <NewBookingModal auth={auth} onClose={() => setAdding(false)} onCreated={refreshAll} />}
      <Tabs tab={tab} setTab={setTab} />
      {tab === "tessere" && <LoyaltyManager auth={auth} />}
      {tab === "email" && <CustomerEmails auth={auth} />}
      {tab === "galleria" && <GalleryManager auth={auth} />}
      {tab === "agenda" && <AdminStats bookings={upcoming} />}

      {tab === "agenda" && view === "week" && <WeekView bookings={week} onCancel={cancel} busyId={busyId} monday={monday} setMonday={setMonday} />}

      {tab === "agenda" && view === "list" && bookings === null && <Loader2 className="w-6 h-6 animate-spin text-[#0B1F3A]" />}
      {tab === "agenda" && view === "list" && bookings && bookings.length === 0 && <p data-testid="admin-empty" className="text-[#6F6466]">Nessuna prenotazione{date ? " per questa data" : " in programma"}.</p>}

      {tab === "agenda" && view === "list" && <div className="space-y-10" data-testid="admin-bookings-list">
        {dates.map((d) => (
          <section key={d}>
            <h2 className="font-display text-2xl font-bold mb-4">{new Date(d + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</h2>
            <div className="rounded-2xl border border-[rgba(11,31,58,0.2)] bg-white divide-y divide-[rgba(26,20,22,0.06)]">
              {groups[d].map((b) => (
                <div key={b.id} data-testid={`admin-booking-${b.id}`} className={`flex flex-wrap items-center gap-4 px-5 py-4 ${b.status === "cancelled" ? "opacity-50" : ""}`}>
                  <span className="font-display font-bold text-xl text-[#0B1F3A] w-16">{b.time}</span>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-semibold">{b.name} <span className="text-[#6F6466] font-normal text-sm">· {b.phone}</span></p>
                    <p className="text-sm text-[#6F6466]">{b.service_names.join(" + ")} · €{euro(b.total)} · #{b.id}{b.source === "manual" ? " · manuale" : ""}{b.note ? ` · ${b.note}` : ""}</p>
                  </div>
                  {b.status === "cancelled" ? (
                    <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#6F6466]"><XCircle className="w-4 h-4" /> Annullata{b.cancelled_by === "customer" ? " dal cliente" : ""}</span>
                  ) : (
                    <button
                      data-testid={`admin-cancel-${b.id}`}
                      onClick={() => cancel(b)}
                      disabled={busyId === b.id}
                      className="rounded-full border border-[#0B1F3A] text-[#0B1F3A] px-4 py-2 text-xs uppercase tracking-wider hover:bg-[#0B1F3A] hover:text-white transition-colors disabled:opacity-40"
                    >
                      Annulla
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>}
    </div>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return setUser(false);
    axios.get(`${API}/auth/me`, auth()).then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    axios.post(`${API}/auth/logout`).catch(() => {});
    setUser(false);
  };

  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B1F3A]" /></div>;
  if (!user) return <Login onLogin={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
}
